/**
 * timelineEdit — Envío AP-3 · reglas puras de edición del timeline.
 *
 * Sin React ni store: imán (pegado y corrimiento), huecos, reemplazo y
 * duplicado. El store las usa y los tests las cubren por separado.
 */

/** Pistas base: los clips van uno detrás de otro y no se superponen. */
export const SEQUENTIAL_TRACKS = ['video', 'image'];
export const isSequential = (trackKind) => SEQUENTIAL_TRACKS.includes(trackKind);

/** Distancia (en px) a la que un borde se engancha al arrastrar. */
export const SNAP_PX = 10;
export const MIN_GAP = 0.3;
const EPS = 0.01;

const round = (n) => Math.round(n * 1000) / 1000;
const end = (c) => c.start_time + c.duration;

/** Huecos entre clips de una pista: [{ start, end, size }]. */
export function findGaps(clips, minGap = MIN_GAP) {
  const sorted = [...(clips || [])].sort((a, b) => a.start_time - b.start_time);
  const gaps = [];
  let cursor = 0;
  sorted.forEach((c) => {
    const size = round(c.start_time - cursor);
    if (size >= minGap) gaps.push({ start: round(cursor), end: round(c.start_time), size });
    cursor = Math.max(cursor, end(c));
  });
  return gaps;
}

/** Corre `delta` segundos todos los clips que empiezan en `from` o después. */
export function shiftFrom(clips, from, delta, exceptId = null) {
  return clips.map((c) => (c.id !== exceptId && c.start_time >= from - EPS
    ? { ...c, start_time: Math.max(0, round(c.start_time + delta)) }
    : c));
}

/** Borra un clip; con el imán, lo que sigue se corre y tapa el hueco. */
export function removeWithRipple(clips, id, { ripple }) {
  const gone = clips.find((c) => c.id === id);
  if (!gone) return { clips, removed: null, closed: 0 };
  let rest = clips.filter((c) => c.id !== id);
  // Sólo se corre si nada más ocupaba ese tramo (en pistas secuenciales nunca pasa).
  if (ripple) rest = shiftFrom(rest, end(gone), -gone.duration);
  return { clips: rest, removed: gone, closed: ripple ? gone.duration : 0 };
}

/** Cierra el hueco que termina en `gapEnd` corriendo lo que sigue. */
export function closeGap(clips, gapEnd, size) {
  return shiftFrom(clips, gapEnd, -size);
}

/**
 * Pegado al arrastrar: si el inicio o el final del clip quedan a menos de
 * `threshold` segundos de un borde, se engancha. Devuelve { start, guide }.
 */
export function snapStart(start, duration, edges, threshold) {
  let best = null;
  (edges || []).forEach((edge) => {
    const dStart = Math.abs(start - edge);
    const dEnd = Math.abs(start + duration - edge);
    if (dStart <= threshold && (!best || dStart < best.dist)) best = { dist: dStart, start: edge, guide: edge };
    if (dEnd <= threshold && (!best || dEnd < best.dist)) best = { dist: dEnd, start: edge - duration, guide: edge };
  });
  if (!best || best.start < 0) return { start: Math.max(0, start), guide: null };
  return { start: round(best.start), guide: round(best.guide) };
}

/** Bordes a los que puede engancharse un clip (menos los suyos). */
export function snapEdges(allClips, exceptId, playhead) {
  const edges = new Set([0]);
  if (Number.isFinite(playhead)) edges.add(round(playhead));
  (allClips || []).forEach((c) => {
    if (c.id === exceptId) return;
    edges.add(round(c.start_time));
    edges.add(round(end(c)));
  });
  return [...edges];
}

/**
 * Reemplaza el contenido de un clip conservando posición, fundidos, volumen
 * y transform. `keepDuration`: el clip mantiene su largo (si el nuevo es más
 * corto, se achica a lo que dura). Si no, toma el largo del nuevo.
 * Devuelve { clip, delta }: delta = cuánto cambió el largo (para correr lo que sigue).
 */
export function replaceContent(clip, meta, { keepDuration = true } = {}) {
  const kind = meta.kind || clip.kind || 'video';
  const media = meta.original_duration || null;
  let duration = clip.duration;
  if (kind !== 'image' && media) {
    duration = keepDuration ? Math.min(clip.duration, media) : media;
  }
  duration = round(Math.max(0.1, duration));
  const next = {
    ...clip,
    source_url: meta.source_url,
    kind,
    label: meta.label || clip.label,
    thumb_url: meta.thumb_url || (kind === 'image' ? meta.source_url : null),
    asset_id: meta.asset_id || null,
    source: meta.source || null,
    original_duration: media,
    trim_start: 0,
    duration,
  };
  return { clip: next, delta: round(duration - clip.duration) };
}

/** Copia que va justo después del original. */
export function duplicateAfter(clip, newId) {
  return { ...clip, id: newId, start_time: round(end(clip)) };
}
